import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const RefundModal = ({ booking, onClose, onRefundSuccess }) => {
  const [refundType, setRefundType] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const totalAmount = parseFloat(booking?.totalAmount || 0);
  const alreadyRefunded = parseFloat(booking?.refundAmount || 0);
  const maxRefundable = totalAmount - alreadyRefunded;

  const refundReasons = [
    { value: 'requested_by_customer', label: 'Requested by renter' },
    { value: 'duplicate', label: 'Duplicate charge' },
    { value: 'fraudulent', label: 'Fraudulent transaction' },
  ];

  const handleSubmit = async () => {
    setError(null);

    const amount = refundType === 'full' ? null : parseFloat(customAmount);

    if (refundType === 'partial') {
      if (!customAmount || isNaN(amount) || amount <= 0) {
        setError('Please enter a valid refund amount.');
        return;
      }
      if (amount > maxRefundable) {
        setError(`Amount cannot exceed $${maxRefundable?.toFixed(2)}.`);
        return;
      }
    }

    setIsProcessing(true);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const token = sessionData?.session?.access_token;

      const { data, error: fnError } = await supabase?.functions?.invoke('process-refund', {
        body: {
          bookingId: booking?.id || booking?.bookingId,
          refundAmount: refundType === 'partial' ? amount : null,
          reason,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fnError || data?.error) {
        throw new Error(data?.error || fnError?.message || 'Refund failed');
      }

      onRefundSuccess?.({
        bookingId: booking?.id || booking?.bookingId,
        refundAmount: data?.refund_amount,
        isFullRefund: data?.is_full_refund,
        message: data?.message,
      });
    } catch (err) {
      console.error('Refund error:', err);
      setError(err?.message || 'Failed to process refund. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
              <Icon name="RotateCcw" size={20} className="text-warning" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-foreground">Issue Refund</h2>
              <p className="font-caption text-xs text-muted-foreground">
                {booking?.renterName} • {booking?.trailerName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Booking Amount Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-body text-muted-foreground">Total Paid</span>
              <span className="font-body font-semibold text-foreground">${totalAmount?.toFixed(2)}</span>
            </div>
            {alreadyRefunded > 0 && (
              <div className="flex justify-between text-sm">
                <span className="font-body text-muted-foreground">Already Refunded</span>
                <span className="font-body font-semibold text-warning">-${alreadyRefunded?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm border-t border-border pt-2">
              <span className="font-body font-medium text-foreground">Max Refundable</span>
              <span className="font-body font-bold text-primary">${maxRefundable?.toFixed(2)}</span>
            </div>
          </div>

          {/* Refund Type */}
          <div>
            <label className="font-body text-sm font-medium text-foreground mb-3 block">
              Refund Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setRefundType('full')}
                className={`p-3 rounded-lg border-2 text-left transition-smooth ${
                  refundType === 'full' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  <Icon
                    name="RefreshCw"
                    size={16}
                    className={refundType === 'full' ? 'text-primary' : 'text-muted-foreground'}
                    strokeWidth={2}
                  />
                  <span className={`font-body text-sm font-medium ${refundType === 'full' ? 'text-primary' : 'text-foreground'}`}>
                    Full Refund
                  </span>
                </div>
                <p className="font-caption text-xs text-muted-foreground">
                  ${maxRefundable?.toFixed(2)}
                </p>
              </button>
              <button
                onClick={() => setRefundType('partial')}
                className={`p-3 rounded-lg border-2 text-left transition-smooth ${
                  refundType === 'partial' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  <Icon
                    name="Scissors"
                    size={16}
                    className={refundType === 'partial' ? 'text-primary' : 'text-muted-foreground'}
                    strokeWidth={2}
                  />
                  <span className={`font-body text-sm font-medium ${refundType === 'partial' ? 'text-primary' : 'text-foreground'}`}>
                    Partial Refund
                  </span>
                </div>
                <p className="font-caption text-xs text-muted-foreground">Custom amount</p>
              </button>
            </div>
          </div>

          {/* Custom Amount Input */}
          {refundType === 'partial' && (
            <div>
              <label className="font-body text-sm font-medium text-foreground mb-2 block">
                Refund Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-body text-sm">$</span>
                <input
                  type="number"
                  min="0.01"
                  max={maxRefundable}
                  step="0.01"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e?.target?.value)}
                  placeholder={`Max $${maxRefundable?.toFixed(2)}`}
                  className="w-full pl-7 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="font-body text-sm font-medium text-foreground mb-2 block">
              Reason for Refund
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              {refundReasons?.map((r) => (
                <option key={r?.value} value={r?.value}>{r?.label}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5 flex-shrink-0" strokeWidth={2} />
              <p className="font-body text-sm text-error">{error}</p>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start space-x-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" strokeWidth={2} />
            <p className="font-caption text-xs text-warning">
              Refunds are processed immediately and cannot be reversed. The renter will receive the funds within 5–10 business days.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={isProcessing}
            iconName="RotateCcw"
            className="bg-warning hover:bg-warning/90 text-white"
          >
            {isProcessing ? 'Processing...' : `Refund ${refundType === 'full' ? `$${maxRefundable?.toFixed(2)}` : customAmount ? `$${parseFloat(customAmount)?.toFixed(2)}` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
